import { fetchAccountData } from "../services/account";
import { LoadingCircle } from "../components/common/loading";
import {
  type ReactNode,
  createContext,
  useContext,
  useEffect,
  useState,
} from "react";

interface AuthContextProps {
  email: string;
  setEmail: (email: string) => void;
  authenticated: boolean;
  setAuthenticated: (authenticated: boolean) => void;
}

interface ErrorWithResponse extends Error {
  response?: {
    status: number;
  };
}

const AuthContext = createContext<AuthContextProps | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [email, setEmail] = useState<string>("");
  const [authenticated, setAuthenticated] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  useEffect(() => {
    const checkAuth = async () => {
      try {
        setLoading(true);

        const accountData = await fetchAccountData();

        if (accountData) {
          setAuthenticated(true);
        } else {
          setAuthenticated(false);
        }

        setLoading(false);
      } catch (error) {
        const err = error as ErrorWithResponse;
        setAuthenticated(false);

        if (err.response && err.response.status === 401) {
          setAuthenticated(false);
        } else {
          console.error("Failed to check authentication", error);
        }
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  return (
    <AuthContext.Provider
      value={{
        email,
        setEmail,
        authenticated,
        setAuthenticated,
      }}
    >
      {loading ? (
        <div className="absolute w-full h-full flex items-center justify-center bg-[#F9FBFF]">
          <LoadingCircle />
        </div>
      ) : (
        children
      )}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextProps => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
